/** Guard for applying the result of a still-current readback. */
export type PickRequestContext = { isCurrent(): boolean };

type WaitingPick = {
	run(): Promise<void>;
	cancel(): void;
	hover: boolean;
	key?: object;
};

/** Serializes picker preparation, readback and effects; coalesces hover and keyed work. */
export class PickScheduler {
	private _queue: WaitingPick[] = [];
	private _running = false;
	private _generation = 0;
	private _hoverVersion = 0;
	private _disposed = false;
	private _idle: (() => void)[] = [];

	public schedule<T>(
		work: (context: PickRequestContext) => Promise<T>,
	): Promise<T | undefined> {
		return this._enqueue(work, false);
	}
	public scheduleHover<T>(
		work: (context: PickRequestContext) => Promise<T>,
	): Promise<T | undefined> {
		++this._hoverVersion;
		for (const item of this._queue.filter((item) => item.hover)) {
			item.cancel();
		}
		this._queue = this._queue.filter((item) => !item.hover);
		return this._enqueue(work, true);
	}
	/** Takes over the FIFO slot of queued work sharing `key`, which resolves undefined; running work is unaffected. */
	public scheduleLatest<T>(
		key: object,
		work: (context: PickRequestContext) => Promise<T>,
	): Promise<T | undefined> {
		const slot = this._queue.findIndex((item) => item.key === key);
		if (slot === -1) {
			return this._enqueue(work, false, key);
		}
		this._queue[slot].cancel();
		return this._enqueue(work, false, key, slot);
	}
	public invalidate(): void {
		++this._generation;
		for (const item of this._queue) {
			item.cancel();
		}
		this._queue = [];
	}
	public whenIdle(): Promise<void> {
		return this._running || this._queue.length
			? new Promise((resolve) => this._idle.push(resolve))
			: Promise.resolve();
	}
	public dispose(): void {
		this._disposed = true;
		this.invalidate();
	}
	private _enqueue<T>(
		work: (context: PickRequestContext) => Promise<T>,
		hover: boolean,
		key?: object,
		slot?: number,
	): Promise<T | undefined> {
		if (this._disposed) {
			return Promise.resolve(undefined);
		}
		const generation = this._generation;
		const version = this._hoverVersion;
		const context = {
			isCurrent: () =>
				!this._disposed &&
				generation === this._generation &&
				(!hover || version === this._hoverVersion),
		};
		return new Promise((resolve, reject) => {
			const item: WaitingPick = {
				hover,
				key,
				cancel: () => resolve(undefined),
				run: async () => {
					try {
						const result = context.isCurrent()
							? await work(context)
							: undefined;
						resolve(context.isCurrent() ? result : undefined);
					} catch (error) {
						reject(error);
					}
				},
			};
			if (slot === undefined) {
				this._queue.push(item);
			} else {
				this._queue[slot] = item;
			}
			this._pump();
		});
	}
	private _pump(): void {
		if (this._running) {
			return;
		}
		const next = this._queue.shift();
		if (!next) {
			for (const resolve of this._idle.splice(0)) {
				resolve();
			}
			return;
		}
		this._running = true;
		void Promise.resolve()
			.then(() => next.run())
			.then(() => {
				this._running = false;
				this._pump();
			});
	}
}
